import { Badge, Button, Card, FormField, Input, Skeleton, Spinner } from '@ezhire/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronUp, MessageSquare, RefreshCw, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSupabaseClient } from '../hooks/use-supabase';

interface AdvisorRunRow {
  id: string;
  content: string;
  ran_at: string;
  data_as_of: string | null;
  expires_at: string | null;
  cost_usd: number;
}

interface AdvisorRow {
  id: string;
  page_route: string;
  name: string;
  status: 'draft' | 'active' | 'inactive' | 'disabled';
  suggested_followups: string[];
}

/** Strip markdown syntax and return the first two meaningful content lines. */
function extractPreview(content: string): string {
  const lines = content
    .split('\n')
    .map((l) =>
      l
        .replace(/^#{1,6}\s+/, '')
        .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^[-*]\s+/, '')
        .replace(/^>\s+/, '')
        .replace(/^---+$/, '')
        .trim()
    )
    .filter((l) => {
      if (!l) return false;
      // skip metadata lines (timestamps, "Data as of …", "Ran at …")
      if (/^data as of/i.test(l)) return false;
      if (/^\d{4}-\d{2}-\d{2}/.test(l)) return false;
      return true;
    });
  return lines.slice(0, 2).join(' · ');
}

/**
 * AdvisorPanel — drop-in right-side panel for any page that wants
 * an AI advisor. Takes the page route, looks up the configured
 * advisor row, shows the cached content (or runs fresh if expired),
 * and exposes a manual re-run + follow-up Q&A mini-session.
 *
 * When the advisor row is status = 'inactive' the panel renders a
 * "not yet available" card without hitting the proxy — that's how
 * Today + Finance Close advisors wait for their pages.
 */
export function AdvisorPanel({ pageRoute, moduleSlug }: { pageRoute: string; moduleSlug: string }) {
  const supabase = useSupabaseClient();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const advisorQuery = useQuery({
    queryKey: ['advisor-panel', pageRoute],
    queryFn: async (): Promise<{ advisor: AdvisorRow | null; latestRun: AdvisorRunRow | null }> => {
      const { data: advisor } = await supabase
        .from('ai_advisors')
        .select('id, page_route, name, status, suggested_followups')
        .eq('page_route', pageRoute)
        .maybeSingle();
      if (!advisor) return { advisor: null, latestRun: null };
      const a = advisor as unknown as AdvisorRow;
      if (a.status !== 'active') return { advisor: a, latestRun: null };
      const { data: run } = await supabase
        .from('ai_advisor_runs')
        .select('id, content, ran_at, data_as_of, expires_at, cost_usd')
        .eq('advisor_id', a.id)
        .order('ran_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return { advisor: a, latestRun: (run as unknown as AdvisorRunRow) ?? null };
    },
  });

  const run = useMutation({
    mutationFn: async ({ advisorId, userQuestion }: { advisorId: string; userQuestion?: string }) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { supabaseUrl, supabaseKey } = supabase as unknown as { supabaseUrl: string; supabaseKey: string };
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: supabaseKey,
        },
        body: JSON.stringify({
          module_slug: moduleSlug,
          advisor_id: advisorId,
          stream: false,
          messages: [
            {
              role: 'user',
              content:
                userQuestion ??
                `Please render the advisor output for page ${pageRoute} using the latest data.`,
            },
          ],
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Request failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advisor-panel', pageRoute] });
    },
  });

  const [followup, setFollowup] = useState('');

  if (advisorQuery.isPending) {
    return (
      <Card>
        <div className="p-4">
          <Skeleton className="h-24 w-full" />
        </div>
      </Card>
    );
  }

  const advisor = advisorQuery.data?.advisor;
  if (!advisor) return null;

  if (advisor.status !== 'active') {
    return (
      <Card>
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-text-muted" />
              <span className="text-body text-text-primary">{advisor.name}</span>
            </div>
            <Badge variant="outline">{advisor.status}</Badge>
          </div>
          <p className="text-caption text-text-muted">
            Advisor is {advisor.status} — it will turn on once its page and data source are live.
          </p>
        </div>
      </Card>
    );
  }

  const latestRun = advisorQuery.data?.latestRun;
  const expired = latestRun?.expires_at ? new Date(latestRun.expires_at) < new Date() : true;

  return (
    <Card>
      <div className="flex flex-col">
        {/* Header — always visible */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-start justify-between gap-3 p-4 text-left"
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 shrink-0 text-accent-500" />
              <span className="text-body font-medium text-text-primary">{advisor.name}</span>
              {latestRun && expired && (
                <Badge variant="outline" className="text-caption">stale</Badge>
              )}
            </div>
            {!expanded && latestRun?.content && (
              <p className="line-clamp-2 pl-6 text-caption text-text-muted">
                {extractPreview(latestRun.content)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* span+role rather than <button>, since the outer card header is
                already a <button> (click-to-expand). Nested <button>s break
                hydration / accessibility. */}
            <span
              role="button"
              tabIndex={0}
              aria-label={run.isPending ? 'Re-running…' : 'Re-run advisor'}
              aria-disabled={run.isPending || !advisor}
              title="Re-run"
              onClick={(e) => {
                e.stopPropagation();
                if (run.isPending || !advisor) return;
                run.mutate({ advisorId: advisor.id });
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.stopPropagation();
                e.preventDefault();
                if (run.isPending || !advisor) return;
                run.mutate({ advisorId: advisor.id });
              }}
              className={[
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent text-text-muted transition-colors',
                'hover:border-border hover:bg-bg-elevated hover:text-text-primary',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
                run.isPending || !advisor ? 'pointer-events-none opacity-50' : 'cursor-pointer',
              ].join(' ')}
            >
              {run.isPending
                ? <Spinner size="sm" />
                : <RefreshCw className="h-3.5 w-3.5" />
              }
            </span>
            {expanded
              ? <ChevronUp className="h-4 w-4 text-text-muted" />
              : <ChevronDown className="h-4 w-4 text-text-muted" />
            }
          </div>
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="flex flex-col gap-4 border-t border-border px-4 pb-4 pt-3">
            {run.isPending && !latestRun ? (
              <div className="flex items-center gap-2 text-text-muted">
                <Spinner size="sm" /> Generating…
              </div>
            ) : latestRun ? (
              <>
                {run.isPending && (
                  <div className="flex items-center gap-2 text-caption text-text-muted">
                    <Spinner size="sm" /> Generating response…
                  </div>
                )}
                {/* Markdown content */}
                <div className="rounded-md bg-surface-raised px-3 py-2">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => (
                        <h1 className="mb-2 text-base font-semibold text-text-primary">{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="mb-1.5 mt-3 text-sm font-semibold text-text-primary first:mt-0">{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="mb-1 mt-2 text-sm font-medium text-text-primary">{children}</h3>
                      ),
                      p: ({ children }) => (
                        <p className="mb-2 text-sm leading-relaxed text-text-secondary last:mb-0">{children}</p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-text-primary">{children}</strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic text-text-muted">{children}</em>
                      ),
                      ul: ({ children }) => (
                        <ul className="mb-2 ml-4 list-disc space-y-0.5 text-sm text-text-secondary">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="mb-2 ml-4 list-decimal space-y-0.5 text-sm text-text-secondary">{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li className="leading-relaxed">{children}</li>
                      ),
                      hr: () => (
                        <hr className="my-3 border-border" />
                      ),
                      code: ({ children }) => (
                        <code className="rounded bg-surface-overlay px-1 py-0.5 font-mono text-xs text-text-primary">{children}</code>
                      ),
                    }}
                  >
                    {latestRun.content || '*(no content)*'}
                  </ReactMarkdown>
                </div>

                {/* Footer: timestamp + feedback + cost */}
                <div className="flex items-center justify-between text-caption text-text-muted">
                  <span>
                    Ran {formatDistanceToNow(new Date(latestRun.ran_at), { addSuffix: true })}
                    {expired ? ' · stale' : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    <FeedbackButtons advisorRunId={latestRun.id} />
                    <span className="font-mono">${latestRun.cost_usd.toFixed(4)}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-caption text-text-muted">
                No cached run yet. Click Re-run to generate the first output.
              </p>
            )}

            {/* Error feedback */}
            {run.error && (
              <p className="rounded-md bg-danger/10 px-3 py-2 text-caption text-danger">
                {(run.error as Error).message}
              </p>
            )}

            {/* Suggested follow-ups */}
            {(advisor.suggested_followups?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2">
                {advisor.suggested_followups.map((q) => (
                  <Button
                    key={q}
                    size="sm"
                    variant="secondary"
                    loading={run.isPending}
                    disabled={run.isPending}
                    onClick={() => run.mutate({ advisorId: advisor.id, userQuestion: q })}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            )}

            {/* Follow-up input */}
            <FormField
              label={
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-3 w-3" /> Ask a follow-up
                </span>
              }
            >
              <div className="flex items-center gap-2">
                <Input
                  value={followup}
                  onChange={(e) => setFollowup(e.target.value)}
                  placeholder="e.g. Why did CPL jump in Abu Dhabi?"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && followup.trim()) {
                      run.mutate({ advisorId: advisor.id, userQuestion: followup });
                      setFollowup('');
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!followup.trim()) return;
                    run.mutate({ advisorId: advisor.id, userQuestion: followup });
                    setFollowup('');
                  }}
                  loading={run.isPending}
                >
                  Ask
                </Button>
              </div>
            </FormField>
          </div>
        )}
      </div>
    </Card>
  );
}

function FeedbackButtons({ advisorRunId }: { advisorRunId: string }) {
  const supabase = useSupabaseClient();
  const [submitted, setSubmitted] = useState<'up' | 'down' | null>(null);

  const submit = async (thumbs: 'up' | 'down') => {
    if (submitted) return;
    setSubmitted(thumbs);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    try {
      // Type escape: org_id is filled by the tg_fill_org_id trigger,
      // but the generated Database type marks it NOT NULL.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase.from('ai_feedback') as any).insert({
        target_type: 'advisor',
        advisor_run_id: advisorRunId,
        user_id: user?.id ?? null,
        thumbs,
      });
    } catch {
      setSubmitted(null);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={() => submit('up')}
        disabled={submitted !== null}
        aria-label="Helpful"
      >
        <ThumbsUp
          className={`h-3 w-3 ${submitted === 'up' ? 'text-accent-500' : 'text-text-muted'}`}
        />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => submit('down')}
        disabled={submitted !== null}
        aria-label="Not helpful"
      >
        <ThumbsDown
          className={`h-3 w-3 ${submitted === 'down' ? 'text-danger' : 'text-text-muted'}`}
        />
      </Button>
    </div>
  );
}
